import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    FluidImage,
    Link,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogNav,
    Typography,
} from '@vp/swan';
import { useUrls } from '../../../../contexts/UrlContext';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { isGenAiLogomakerAvailable } from '../../../../utils/featureAvailability';
import type { Design } from '../../content/logo-inspiration';

interface ImageDetailsModalProps {
    design: Design | null;
    onDismiss: () => void;
}

const ImageDetailsModal = ({ design, onDismiss }: ImageDetailsModalProps) => {
    const { i18n } = useTranslation();
    const { logoMakerUrl, logoDesignUrl } = useUrls();
    const locale = sanitiseLocale(i18n.language);

    const logomakerCtaRef = useTrackNavigationClicked(
        'Logo Ideas',
        'DetailsModal',
        logoMakerUrl ?? '',
        'createYourFreelogo',
    );

    const logoDesignCtaRef = useTrackNavigationClicked(
        'Logo Ideas',
        'DetailsModal',
        logoDesignUrl ?? '',
        'exploreYourOptions',
    );

    const designTitle = `Logo designed by ${design?.designer.displayName}`;

    return (
        <ModalDialog
            variant="panel-right"
            isOpen={!!design}
            onRequestDismiss={() => onDismiss()}
            id="modal-dialog"
            aria-label={designTitle}
        >
            <ModalDialogContent aria-labelledby="modal-dialog">
                <ModalDialogNav>
                    {/* TODO: Deprecated prop accessibleText */}
                    {/* eslint-disable-next-line @vp/swan/no-deprecated */}
                    <ModalDialogCloseButton accessibleText="Close" />
                </ModalDialogNav>
                <ModalDialogBody paddingBottom={{ xs: 5, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <FluidImage
                        src={design?.publicUrl}
                        alt={design?.title || ''}
                        key={design?.publicUrl}
                        marginY="7"
                    />
                    <Typography fontSkin={'body-small-bold'}>
                        Logo designed by {design?.designer.displayName}
                    </Typography>
                    {design?.contestWinner && (
                        <Callout marginTop={'5'}>Logo design contest winner</Callout>
                    )}
                </ModalDialogBody>
                <ModalDialogFooter pinned>
                    <ModalDialogButtons>
                        <Button
                            skin="primary"
                            width="full-width"
                            render={(props) => (
                                <Link
                                    href={logoMakerUrl}
                                    className={props.className}
                                    ref={logomakerCtaRef}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            {isGenAiLogomakerAvailable(locale)
                                ? `Create your free AI logo`
                                : `Try free Logomaker`}
                        </Button>
                        <Button
                            skin="secondary"
                            width="full-width"
                            render={(props) => (
                                <Link
                                    href={logoDesignUrl}
                                    className={props.className}
                                    ref={logoDesignCtaRef}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            Explore all logo services
                        </Button>
                    </ModalDialogButtons>
                </ModalDialogFooter>
            </ModalDialogContent>
        </ModalDialog>
    );
};

export default ImageDetailsModal;
